import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import utils from '../helpers/utils';
import _has from 'lodash/has';
import {
  equifaxClient,
  getCreditPullInfo,
  getTuCreditReportHistory,
  setCreditOnDemand,
  setProviderBureauData,
} from '../actions/credit';
import useJWT from './useJWT';
import {RootState} from '../reducers/rootReducer';
import {stringBundle, getLangCode} from '../stringBundle';
import {processCreditEquifaxPull} from '../helpers/creditHelper';

/**
 * Custom hook to handle credit data fetching and processing
 * @returns Credit data related methods and state
 */
export const useCreditData = () => {
  const dispatch = useDispatch();
  const auth = useSelector((store: RootState) => store.auth);
  const [hasDispatched, setHasDispatched] = useState(false);
  const accountId = utils.safeGet(auth, 'user.primaryMember.accountId', '') || '';
  const [jwtEnabled, setJWTEnabled] = useState(false);
  const {
    isSuccess: isJWTSuccess,
    isError: isJWTError,
    data: jwt,
    error: jwtError,
  } = useJWT(accountId, jwtEnabled, 'app');

  // Function to fetch credit data from Equifax
  const equifaxCredit = useCallback(
    (is3BUser: boolean, creditPullInfo: $TSFixMe, accountId: string) => {
      if ((isJWTSuccess && !window.REACT_APP_ENABLE_MOCK_CREDIT_TOOL) || window.REACT_APP_ENABLE_MOCK_CREDIT_TOOL) {
        if (!hasDispatched) {
          const showLLOnboardingCredit =
            utils.safeGet(auth, 'user.primaryMember.showLLOnboardingCredit', false) || false;
          const showLLDormantOnboarding =
            utils.safeGet(auth, 'user.primaryMember.showLLDormantOnboarding', false) || false;
          const isPull1BReportAllowed = utils.safeGet(creditPullInfo, 'isPull1BReportAllowed', false) || false;
          const localizedStringBundle = stringBundle(getLangCode(), null);

          // pull latest credit score and report for dormant/reengage onboarding user if it is available
          if (isPull1BReportAllowed && (showLLOnboardingCredit || showLLDormantOnboarding)) {
            processCreditEquifaxPull(creditPullInfo, 'EFX', is3BUser, {dispatch, auth}, localizedStringBundle, jwt);
          } else {
            dispatch(equifaxClient(jwt, localizedStringBundle, getLangCode(), is3BUser, creditPullInfo, accountId));
          }
          return setHasDispatched(true);
        }
      } else if (isJWTError) {
        return dispatch({
          type: 'EQUIFAX_STATUS',
          equifax_status: 'error',
          rawError: jwtError,
        });
      }
    },
    [dispatch, isJWTSuccess, isJWTError, jwtError, jwt, hasDispatched, auth]
  );

  // Function to initialize Equifax credit data
  const initCreditDataForEquifax = useCallback(async() => {
    const is3BScoreFeatureAvailable =
      utils.safeGet(auth, 'user.primaryMember.plan.productFeatures.creditScore_3B.featureStatus') === 'AVAILABLE';
    const is3BScoreFulfillmentEnrolled =
      utils.safeGet(auth, 'user.primaryMember.plan.productFeatures.creditScore_3B.fulfillmentStatus') === 'ENROLLED';
    const is3BUser = is3BScoreFeatureAvailable && is3BScoreFulfillmentEnrolled;
    const accountId = utils.safeGet(auth, 'user.primaryMember.accountId', '') || '';
    dispatch(setCreditOnDemand());

    try {
      const res: $TSFixMe = await dispatch(getCreditPullInfo(accountId));
      const isFulfilled1BReportAvailable = utils.safeGet(res, 'pullInfo.isFulfilled1BReportAvailable', false);
      const isFulfilled3BReportAvailable = utils.safeGet(res, 'pullInfo.isFulfilled3BReportAvailable', false);
      const creditPullInfo = utils.safeGet(res, 'pullInfo', {});
      if (isFulfilled1BReportAvailable || isFulfilled3BReportAvailable || window.REACT_APP_ENABLE_MOCK_CREDIT_TOOL) {
        equifaxCredit(is3BUser, creditPullInfo, accountId);
      }
    } catch (error) {
      console.error('Error fetching credit pull info:', error);
    }
  }, [auth, dispatch, equifaxCredit]);

  // Function to initialize credit data
  const initCreditData = useCallback(() => {
    const isLoading = utils.safeGet(auth, 'isLoading');
    if (!isLoading) {
      const productFeatures = utils.safeGet(auth, 'user.primaryMember.plan.productFeatures', '');
      const creditScore1BFeatureStatus = utils.safeGet(productFeatures, 'creditScore_1B.featureStatus', '');
      const creditScore1BFulfillmentStatus = utils.safeGet(productFeatures, 'creditScore_1B.fulfillmentStatus', '');
      const creditScore3BFeatureStatus = utils.safeGet(productFeatures, 'creditScore_3B.featureStatus', '');
      const creditScore3BFullfilmentStatus = utils.safeGet(productFeatures, 'creditScore_3B.fulfillmentStatus', '');
      const creditScore1bCaFeatureStatus = utils.safeGet(productFeatures, 'creditScore1bCa.featureStatus', '');
      const creditScore1bCaFullfilmentStatus = utils.safeGet(productFeatures, 'creditScore1bCa.fulfillmentStatus', '');
      const hasEquifaxCredit =
        creditScore1bCaFeatureStatus !== 'AVAILABLE' &&
        ((creditScore1BFeatureStatus === 'AVAILABLE' && creditScore1BFulfillmentStatus === 'ENROLLED') ||
          (creditScore3BFeatureStatus === 'AVAILABLE' && creditScore3BFullfilmentStatus === 'ENROLLED'));

      const localizedStringBundle = stringBundle(getLangCode(), null);

      if (hasEquifaxCredit && accountId) {
        setJWTEnabled(true);
      } else if (creditScore1bCaFeatureStatus !== 'NOT_AVAILABLE') {
        dispatch(setProviderBureauData('TU', '900'));
        if (creditScore1bCaFeatureStatus === 'AVAILABLE' && creditScore1bCaFullfilmentStatus === 'ENROLLED') {
          dispatch(getTuCreditReportHistory('transunion', localizedStringBundle, getLangCode(), accountId));
        }
      }
    }
  }, [auth, dispatch, accountId]);

  // Call Equifax endpoints for credit data once JWT is available
  useEffect(() => {
    const jwtCheck = !isJWTError && jwt && !_has(jwt, 'jwt.jwtToken');
    if (jwtCheck) {
      initCreditDataForEquifax();
    }
  }, [jwtEnabled, jwt, isJWTError, initCreditDataForEquifax]);

  return {
    initCreditData,
    initCreditDataForEquifax,
    jwtEnabled,
    isJWTSuccess,
    isJWTError,
    jwt,
    jwtError,
  };
};
